import React from 'react';
import { 
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Paper,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardMedia
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
// Import logo and images
import logo from './images/DataPrivyLogo2.png';
import visualizationImage from './images/Buckets.png';

const DataPrivySite = () => {
  // Simple scrolling function using element.scrollIntoView with offset adjustment
  const scrollToSection = (section) => {
    // Find the element
    const element = document.getElementById(section);
    if (!element) return;
    
    // Scroll to element
    element.scrollIntoView({behavior: 'smooth'});
    
    // Apply offset after scrolling
    setTimeout(() => {
      const yOffset = -80; // 80px offset from the top
      const y = window.scrollY + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }, 100);
  };

  return (
    <Box sx={{ 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh', 
      margin: 0,
      padding: 0,
      position: 'relative',
      overflowX: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      width: '100%'
    }}>
        {/* Hero Section */}
        <Box 
          id="home"
          sx={{ 
            pt: { xs: 8, sm: 10 },
            pb: 6,
            mt: 0,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundImage: 'linear-gradient(rgba(25, 118, 210, 0.05), rgba(25, 118, 210, 0.1))'
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <img src={logo} alt="DataPrivy Logo" style={{ height: '50px' }} />
                </Box>
                <Typography variant="h3" gutterBottom color="primary">
                  Understanding your data sensitivity in the cloud, one scan at a time
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  DataPrivy is a powerful tool designed to help organizations discover sensitive data stored in their 
                  Amazon S3 buckets and PostgreSQL databases, ensure compliance with privacy regulations, 
                  and protect their most valuable information assets.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button 
                    variant="contained" 
                    size="large" 
                    color="primary"
                    onClick={() => scrollToSection('features')}
                  >
                    Learn More
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 0, 
                  backgroundColor: 'white', 
                  borderRadius: 2, 
                  boxShadow: 3,
                  height: 'auto',
                  overflow: 'hidden'
                }}>
                  <img 
                    src={visualizationImage} 
                    alt="Data Privacy Visualization" 
                    style={{ 
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Button 
                color="primary" 
                endIcon={<ArrowDownwardIcon />}
                onClick={() => scrollToSection('features')}
              >
                View Key Features
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Features Section */}
        <Box 
          id="features"
          sx={{ 
            py: 8,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: 'white'
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h3" gutterBottom sx={{ mb: 4, textAlign: 'center' }} color="primary">
              Key Features
            </Typography>
            <Grid container spacing={4}>
              {/* Feature 1 */}
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    sx={{ height: 140, backgroundColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <StorageIcon sx={{ fontSize: 60, color: 'white' }} />
                  </CardMedia>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="div" gutterBottom>
                      Comprehensive Data Source Scanning
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically scan Amazon S3 buckets and PostgreSQL databases to identify files and database records 
                      containing sensitive information.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" color="primary" onClick={() => scrollToSection('how-it-works')}>
                      Learn More
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Feature 2 */}
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    sx={{ height: 140, backgroundColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <SearchIcon sx={{ fontSize: 60, color: 'white' }} />
                  </CardMedia>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="div" gutterBottom>
                      Detailed Scan Results
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Get comprehensive reports that identify sensitive data categories, compliance violations, 
                      and data sensitivity levels across your S3 storage and database systems.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" color="primary" onClick={() => scrollToSection('how-it-works')}>
                      Learn More
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Feature 3 */}
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    sx={{ height: 140, backgroundColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ScheduleIcon sx={{ fontSize: 60, color: 'white' }} />
                  </CardMedia>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="div" gutterBottom>
                      Scheduled Scans
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Set up regular automated scans to continuously monitor your data across S3 buckets and PostgreSQL databases 
                      to maintain compliance with evolving regulations.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" color="primary" onClick={() => scrollToSection('how-it-works')}>
                      Learn More
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Feature 4 */}
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    sx={{ height: 140, backgroundColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <LockIcon sx={{ fontSize: 60, color: 'white' }} />
                  </CardMedia>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="div" gutterBottom>
                      Compliance Reporting
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose from a variety of compliance configurations to generate reports for regulations like GDPR, HIPAA, and CCPA.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" color="primary" onClick={() => scrollToSection('how-it-works')}>
                      Learn More
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Button 
                color="primary" 
                endIcon={<ArrowDownwardIcon />}
                onClick={() => scrollToSection('regulations')}
              >
                View Compliance Regulations
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Regulations Section */}
        <Box 
          id="regulations"
          sx={{ 
            py: 8,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ pt: 5 }}>
              <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 400 }}>
                DataPrivy can help your organization meet global compliance regulations.
              </Typography>
            </Box>
            
            <Grid container spacing={4} sx={{ mt: 2 }}>
              {/* GDPR Card */}
              <Grid item xs={12} md={4} sx={{ mb: 4 }}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2, 
                    backgroundColor: 'white',
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                      EUROPEAN UNION
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    General Data Protection Regulation (GDPR)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
                    Comprehensive regulation that protects EU citizens' personal data and privacy. 
                    DataPrivy can identify GDPR-relevant data and helps ensure your compliance by detecting 
                    personally identifiable information, sensitive personal data, and data subject to special category protections.
                  </Typography>
                </Paper>
              </Grid>

              {/* HIPAA Card */}
              <Grid item xs={12} md={4} sx={{ mb: 4 }}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2, 
                    backgroundColor: 'white',
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                      UNITED STATES - HEALTHCARE
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    HIPAA
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
                    Critical for health insurers, HIPAA protects patient health information from unauthorized disclosure. 
                    DataPrivy scans your databases and storage for protected health information (PHI), 
                    helping insurance companies identify HIPAA-regulated data in claims processing systems, 
                    customer records, and third-party data exchanges to prevent costly violations.
                  </Typography>
                </Paper>
              </Grid>

              {/* CCPA Card */}
              <Grid item xs={12} md={4} sx={{ mb: 4 }}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2, 
                    backgroundColor: 'white',
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                      UNITED STATES
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    California Consumer Privacy Act (CCPA)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
                    Enhances privacy rights for California residents, including the right to know what 
                    personal information is collected. DataPrivy helps identify data subject to CCPA 
                    requirements, enabling you to respond to consumer requests and maintain compliance.
                  </Typography>
                </Paper>
              </Grid>
              
              {/* CPRA Card */}
              <Grid item xs={12} md={4} sx={{ mb: 4 }}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2, 
                    backgroundColor: 'white',
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                      UNITED STATES
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    California Privacy Rights Act (CPRA)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
                    Expands upon CCPA with additional consumer rights and business obligations. 
                    DataPrivy helps organizations meet CPRA's stricter requirements for sensitive personal information, 
                    data minimization principles, and enhanced consumer rights to correct inaccurate personal information.
                  </Typography>
                </Paper>
              </Grid>

              {/* NAIC Model Law Card */}
              <Grid item xs={12} md={4} sx={{ mb: 4 }}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2, 
                    backgroundColor: 'white',
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                      UNITED STATES - INSURANCE
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    NAIC Insurance Data Security Model Law
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
                    Adopted by many states, this law establishes standards for data security and breach notification 
                    specifically for insurance companies. DataPrivy helps insurers meet requirements for information security 
                    programs, risk assessments, and data classification, ensuring sensitive policyholder information is properly identified and protected.
                  </Typography>
                </Paper>
              </Grid>

              {/* GLBA Card */}
              <Grid item xs={12} md={4} sx={{ mb: 4 }}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2, 
                    backgroundColor: 'white',
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                      UNITED STATES - FINANCIAL
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Gramm-Leach-Bliley Act (GLBA)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
                    Requires financial institutions including insurance companies to explain their information-sharing practices and safeguard sensitive data.
                    DataPrivy helps insurers maintain GLBA compliance by identifying and categorizing nonpublic personal information (NPI) across cloud systems,
                    supporting both the Privacy Rule and Safeguards Rule requirements for protecting customer information.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Button 
                color="primary" 
                endIcon={<ArrowDownwardIcon />}
                onClick={() => scrollToSection('how-it-works')}
              >
                Learn How DataPrivy Works
              </Button>
            </Box>
          </Container>
        </Box>

        {/* How It Works Section */}
        <Box 
          id="how-it-works"
          sx={{ 
            py: 8,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: 'background.default'
          }}
        >
          <Container maxWidth="lg">
            <Paper elevation={3} sx={{ p: 4 }}>
              <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4 }}>
                How DataPrivy Works
              </Typography>
              <Divider sx={{ mb: 4 }} />
              
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                DataPrivy works by leveraging AWS Lambda functions to scan both S3 buckets and PostgreSQL databases for sensitive information. 
                When a user initiates a scan or when an automated schedule triggers one, the system deploys serverless Lambda functions that 
                analyze the content using direct matching and fuzzy string approximation techniques, identifying personal data, financial 
                information, health records, and other sensitive data types.
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                Results are then categorized, mapped to relevant compliance regulations (like GDPR, HIPAA, and PCI DSS), and presented in an 
                intuitive dashboard that helps organizations take action to protect their data and maintain regulatory compliance.
              </Typography>
            </Paper>
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Button 
                color="primary" 
                endIcon={<ArrowDownwardIcon />}
                onClick={() => scrollToSection('faq')}
              >
                View Frequently Asked Questions
              </Button>
            </Box>
          </Container>
        </Box>

        {/* FAQ Section */}
        <Box 
          id="faq"
          sx={{ 
            py: 8,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: 'white'
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h3" gutterBottom sx={{ mb: 4, textAlign: 'center' }} color="primary">
              Frequently Asked Questions
            </Typography>
            <Paper elevation={3} sx={{ p: 4 }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">What types of sensitive data can DataPrivy detect?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    DataPrivy operates on column string matching and fuzzy string approximation. This means that 
                    it can detect any type of sensitive data specified in the configuration files without actually 
                    reading the data itself. DataPrivy relies on the structure of files and databases to identify 
                    potential sensitive information patterns.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">Is my data secure during the scanning process?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Yes, DataPrivy operates entirely within your AWS Cloud environment and never pulls the actual data. 
                    It uses pattern matching and structural analysis to identify potential sensitive data locations 
                    without extracting or storing the sensitive information itself.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">Which compliance regulations does DataPrivy help with?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    DataPrivy helps with various regulations including GDPR, CCPA/CPRA, PCI-DSS, HIPAA, SOX, 
                    and other data protection and privacy regulations by identifying where sensitive data subject 
                    to these regulations might exist in your storage.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">How often should I scan my data sources?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    The frequency depends on your data usage patterns. For environments with frequent data changes, 
                    weekly scans may be appropriate. For more static environments, monthly scans might be sufficient. 
                    Critical storage containing sensitive information should be scanned more frequently.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">What file formats and data structures can DataPrivy scan?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    DataPrivy currently works with CSV files in S3 buckets and with RDS PostgreSQL databases. 
                    The system is designed to analyze structured data where column names and patterns can be 
                    matched against configuration rules to identify sensitive information.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Container>
        </Box>

        {/* Footer */}
        <Box sx={{ py: 3, backgroundColor: 'grey.900', color: 'white' }}>
          <Container maxWidth="lg">
            <Grid container justifyContent="center" direction="column" spacing={2} alignItems="center">
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={logo} alt="DataPrivy Logo" style={{ height: '30px', filter: 'brightness(0) invert(1)' }} />
                </Box>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="grey.400" align="center">
                  © {new Date().getFullYear()} DataPrivy. All rights reserved.
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
  );
};

export default DataPrivySite;
